import { getFileName } from "@helpers/getFileName"
import { useQuery } from "@tanstack/react-query"
import { requestApi2 } from "@utils/http"
import { endpoints, type unisea } from "api"
import { useMemo } from "react"
import {
	FISH_LOG_LOOKBACK_DAYS_DEFAULT,
	getFishLogQueryStartWithLookback,
	normalizeFishLogEvents,
} from "../fishLogEvents"

type FishLogEventsQueryArgs = {
	plantId: number
	gte: Date
	lt: Date
	enabled?: boolean
}

export function useFishLogEventsQuery(args: FishLogEventsQueryArgs) {
	return useQuery({
		queryKey: [
			`source=${getFileName()}`,
			`queryId=FishLogEvents`,
			`endpoints=[${endpoints.getFishLogEvents.getName()}]`,
			`plantId=${args.plantId}`,
			`gte=${args.gte?.toISOString !== undefined ? args.gte?.toISOString() : args.gte}`,
			`lt=${args.lt?.toISOString !== undefined ? args.lt?.toISOString() : args.lt}`,
		],
		queryFn: async (): Promise<unisea.GetFishLogEventsResponse> => {
			const response = await requestApi2(endpoints.getFishLogEvents, {
				plantId: args.plantId,
				interval: {
					gte: args.gte,
					lt: args.lt,
				},
			})
			return response !== null ? response : { events: [] }
		},
		enabled: args.enabled ?? true,
		staleTime: 600000,
		refetchInterval: 600000,
	})
}

/** Pass `enabled: true` only when `PortalFeature.AQUACULTURE` applies (callers use `isFeatureAvailable`). */
type UseFishLogEventsWithLookbackArgs = {
	plantId: number | undefined
	company: string | undefined
	gte: Date | undefined
	lt: Date | undefined
	enabled?: boolean
	lookbackDays?: number
}

export function useFishLogEventsWithLookback(args: UseFishLogEventsWithLookbackArgs) {
	const gteWithLookback = useMemo(
		() =>
			getFishLogQueryStartWithLookback(
				args.gte,
				args.lookbackDays ?? FISH_LOG_LOOKBACK_DAYS_DEFAULT
			),
		[args.gte, args.lookbackDays]
	)

	const shouldLoadFishLogEvents =
		args.enabled === true &&
		args.plantId !== undefined &&
		gteWithLookback !== undefined &&
		args.lt !== undefined

	const query = useFishLogEventsQuery({
		plantId: args.plantId ?? -1,
		gte: gteWithLookback ?? new Date(0),
		lt: args.lt ?? new Date(0),
		enabled: shouldLoadFishLogEvents,
	})

	const normalizedEvents = useMemo(
		() => normalizeFishLogEvents(query.data?.events ?? []),
		[query.data]
	)

	return {
		...query,
		normalizedEvents,
		gteWithLookback,
		shouldLoadFishLogEvents,
	}
}
